import * as React from "react";
import { FaChevronRight } from "react-icons/fa";
import styled from "styled-components";
import { AnalyticsTabs } from "./AnalyticsTabs";
import { AnalyticsViews } from "../../../App/Analytics/Common/index";
import { IFilterCriteriaOwnProps, FilterCriteria } from "./FilterCriteria/FilterCriteria";
import { CourseGraduationCapIcon, CircleIcon, PencilIcon } from "../../utils/Icons/Analytics/TitleIcons";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { Creators as AllCreators } from "../../../actions";
import { IAction } from "reduxsauce";
import { IUser, IUsersAnalytics } from "../../../App/Analytics/Common";

const Creators = (AllCreators as any).App.Users;

const AnalyticsPageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const AnalyticsInnerContainer = styled.div`
  margin: 1.2rem 2rem;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: fit-content;
  gap: 1rem;
  font-size: 1.2rem;

  @media (${({ theme }) => theme.breakpoints.min800}) {
    font-size: 1.17em;
  }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 23px;
  height: 20px;

  > .circle-icon {
    max-width: 1em;
  }
`;

const RightArrowIconContainer = styled.div`
  width: 23px;
  height: 20px;
  margin: 5px 1rem 0;
`;

const Title = styled.h2`
  margin: 0;
`;

const HeaderContainer = styled.div`
  position: sticky;
  top: 5rem;
  z-index: 4;
  background: ${props => props.theme.colors.white};
  margin: 1rem 1.25rem 0 1.5rem;

  @media (${props => props.theme.breakpoints.min800}) {
    top: 7rem;
  }
`;

interface IAnalyticsOwnProps extends IFilterCriteriaOwnProps {
  allUsersCount: number;
  selectedCount: number;
  courseDisplayName: string;
  objectDisplayName: string;
  onPrint: () => void;
  selectedUnitName: string | null;
  backToClassStatistics: () => void;
  selectedAssessmentName: string | null;
  backToUnitAnalytics: () => void;
  changeAnalyticsView: (view: AnalyticsViews) => () => void;
  view: AnalyticsViews;
  classOrScormIcon: React.ReactNode;
  users: IUser[];
  usersAnalytics: IUsersAnalytics;
  selected: Set<string>;
  selectedFromGraph: Set<string>;
  showFilteredTable: boolean;
  toggleShowFilteredTable: () => void;
}

interface IUsersDispatchProps {
  exportUsers: (users: IUser[], usersAnalytics: IUsersAnalytics, isXLSX: boolean) => void;
}

interface INavigationBarProps {
  backToClassStatistics: () => void;
  backToUnitAnalytics: () => void;
  courseDisplayName: string;
  selectedUnitName: string | null;
  selectedAssessmentName: string | null;
}

const UnitBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;

  div {
    display: flex;
    align-items: center;
  }
`;

const ClickableBar = styled.div`
  display: flex;
  flex-direction: row;
  cursor: pointer;
`;

type IAnalyticsProps = IAnalyticsOwnProps & IUsersDispatchProps;

class Analytics extends React.Component<IAnalyticsProps, {}> {
  public render() {
    const {
      objectDisplayName,
      allUsersCount,
      backToClassStatistics,
      backToUnitAnalytics,
      selectedCount,
      filters,
      onRemoveFilter,
      onClearAllFilters,
      onRemoveLabelAndCondition,
      changeAnalyticsView,
      view,
      courseDisplayName,
      classOrScormIcon,
      exportUsers,
      users,
      usersAnalytics,
      selected,
      selectedFromGraph,
      selectedUnitName,
      selectedAssessmentName,
      showFilteredTable,
      toggleShowFilteredTable,
    } = this.props;
    return (
      <AnalyticsPageContainer>
        <HeaderContainer>
          <AnalyticsTitle title={objectDisplayName} icon={classOrScormIcon} />
          <NavigationBar
            backToClassStatistics={backToClassStatistics}
            backToUnitAnalytics={backToUnitAnalytics}
            courseDisplayName={courseDisplayName}
            selectedUnitName={selectedUnitName}
            selectedAssessmentName={selectedAssessmentName}
          />
          <AnalyticsTabs changeAnalyticsView={changeAnalyticsView} view={view} />
          <FilterCriteria
            onRemoveFilter={onRemoveFilter}
            onRemoveLabelAndCondition={onRemoveLabelAndCondition}
            onClearAllFilters={onClearAllFilters}
            filters={filters}
            onPrint={this.onPrint}
            selectedCount={selectedCount}
            allUsersCount={allUsersCount}
            onExport={exportUsers}
            users={users}
            usersAnalytics={usersAnalytics}
            selected={selected}
            selectedFromGraph={selectedFromGraph}
            view={view}
            showFilteredTable={showFilteredTable}
            toggleShowFilteredTable={toggleShowFilteredTable}
          />
        </HeaderContainer>
        <AnalyticsInnerContainer>{this.props.children}</AnalyticsInnerContainer>
      </AnalyticsPageContainer>
    );
  }

  private onPrint = (e: any) => {
    e.preventDefault();
    this.props.onPrint();
  };
}

interface AnalyticsTitleProps {
  title: string;
  icon: React.ReactNode;
}

const AnalyticsTitle: React.FC<AnalyticsTitleProps> = ({ title, icon }) => {
  return (
    <TitleContainer>
      <IconContainer>{icon}</IconContainer>
      <Title>{title}</Title>
    </TitleContainer>
  );
};

const NavigationBar: React.FC<INavigationBarProps> = ({
  backToClassStatistics,
  backToUnitAnalytics,
  courseDisplayName,
  selectedUnitName,
  selectedAssessmentName,
}) => {
  if (selectedUnitName === null) {
    return <AnalyticsTitle title={courseDisplayName} icon={<CourseGraduationCapIcon />} />;
  }
  if (selectedAssessmentName === null) {
    return (
      <UnitBar>
        <ClickableBar onClick={backToClassStatistics}>
          <AnalyticsTitle title={courseDisplayName} icon={<CourseGraduationCapIcon />} />
        </ClickableBar>
        <RightArrowIconContainer>
          <FaChevronRight style={{ width: "100%" }} />
        </RightArrowIconContainer>
        <div style={{ marginTop: "5px" }}>
          <AnalyticsTitle title={selectedUnitName} icon={<CircleIcon className="circle-icon" />} />
        </div>
      </UnitBar>
    );
  }
  return (
    <UnitBar>
      <ClickableBar onClick={backToClassStatistics}>
        <AnalyticsTitle title={courseDisplayName} icon={<CourseGraduationCapIcon />} />
      </ClickableBar>
      <RightArrowIconContainer>
        <FaChevronRight style={{ width: "100%" }} />
      </RightArrowIconContainer>
      <ClickableBar onClick={backToUnitAnalytics}>
        <div style={{ marginTop: "5px" }}>
          <AnalyticsTitle title={selectedUnitName} icon={<CircleIcon className="circle-icon" />} />
        </div>
      </ClickableBar>
      <RightArrowIconContainer>
        <FaChevronRight style={{ width: "100%" }} />
      </RightArrowIconContainer>
      <div style={{ marginTop: "5px" }}>
        <AnalyticsTitle title={selectedAssessmentName} icon={<PencilIcon />} />
      </div>
    </UnitBar>
  );
};

export default connect<{}, IUsersDispatchProps, IAnalyticsOwnProps, {}>(
  (state: {}, ownProps: IAnalyticsOwnProps) => ({}),
  (dispatch: Dispatch<IAction>, ownProps: IAnalyticsOwnProps) => ({
    exportUsers: (users, usersAnalytics, isXLSX) =>
      dispatch(
        Creators.exportUsers(users, usersAnalytics, ownProps.objectDisplayName, ownProps.courseDisplayName, isXLSX)
      ),
  })
)(Analytics);
